import { useEffect, useState } from "react";
import { getDentistViewImages } from "../../Services/APIs/report";
import Loader from "../ClaraView/Result/loader"; // Reusing the loader
import { useParams } from "react-router-dom";
import { logEvent } from "../../Utils/logEvent";

const DentistView = () => {
    const { uid, imageName } = useParams();
    const company = localStorage.getItem("company") || "claraView";
    const [progress, setProgress] = useState(50); // Default position at 50%
    const [imageCropSrc, setCropImageSrc] = useState(null);
    const [imageComboSrc, setComboImageSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);


    const handleSliderChange = (e) => {
        setProgress(e.target.value);
    };

    // Helper function for safe image data conversion
    const safelyProcessImageData = (imageData, setImageState, label) => {
        if (!imageData?.data) {
            logEvent({ app: company, uid, event: `${label} image data missing` });
            return;
        }

        try {
            // If it's already a string (possibly base64), use it directly
            if (typeof imageData.data === 'string') {
                setImageState(`data:image/jpeg;base64,${imageData.data}`);
                return;
            }

            // For ArrayBuffer/byte-array data, wrap it in a Blob rather than
            // base64-encoding it - avoids the ~33% size inflation and the
            // String.fromCharCode/btoa CPU cost of manual encoding.
            const bytes = new Uint8Array(imageData.data);
            const blob = new Blob([bytes], { type: 'image/jpeg' });
            setImageState(URL.createObjectURL(blob));
        } catch (err) {
            logEvent({ app: company, uid, event: `${label} image decode failed`, error: err });
        }
    };

    // Load and process images from API
    useEffect(() => {
        if (!uid || !imageName) {
            logEvent({ app: company, uid, event: "missing uid or image name" });
            setIsLoading(false);
            setError("Missing user ID or image name");
            return;
        }

        setIsLoading(true);
        getDentistViewImages(uid, imageName)
            .then((res) => {
                const { cropData, correctionData } = res.data.data;
                logEvent({ app: company, uid, event: "dentist view images request succeeded" });

                // Process each image separately with error handling - a
                // single image's decode failure doesn't set the page-level
                // `error`, so the other image still renders.
                safelyProcessImageData(cropData, setCropImageSrc, "crop");
                safelyProcessImageData(correctionData, setComboImageSrc, "combo");

                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                const status = err.response?.status;
                if (status === 404) {
                    setError("Image file is not found.");
                    logEvent({ app: company, uid, event: "dentist view images not found (404)", error: err });
                } else {
                    setError("Failed to load images");
                    logEvent({ app: company, uid, event: "dentist view images request failed", error: err });
                }
            });

    }, [uid, imageName]);

    // Revoke Blob URLs once they're replaced or the component unmounts.
    useEffect(() => {
        return () => {
            if (imageCropSrc?.startsWith('blob:')) URL.revokeObjectURL(imageCropSrc);
            if (imageComboSrc?.startsWith('blob:')) URL.revokeObjectURL(imageComboSrc);
        };
    }, [imageCropSrc, imageComboSrc]);

    return (
        <div className='h-dvh font-novatica flex flex-col'>
            {isLoading ? <Loader /> :
                <>
                    <div className='text-center px-2 pt-8 font-medium text-xl   tracking-wider text-[#2C2D24] md:text-3xl'>
                        Patient Smile Assessment
                    </div>
                    <div className='text-center font-normal text-base text-claraview-tertiary pb-4 md:text-xl mx-10 mt-3'>
                        Review the simulated before-and-after results for the patient.
                    </div>



                    {/* Image comparing Section (Flex Grow) */}
                    <div className="flex-grow m-4 flex flex-col">
                        <div className="image-slider-container flex-grow flex flex-col">
                            <div className="image-container flex-grow relative" style={{ height: '400px' }}>
                                <div
                                    className="stacked-images relative w-full h-full rounded-xl"
                                    style={{ maxHeight: '100%', overflow: "hidden" }}>

                                    {error ? (
                                        <div className="w-full h-full flex items-center justify-center text-red-500  ">{error}</div>
                                    ) : (
                                        <>
                                            <div className=" absolute top-0 left-0 w-full h-full rounded-xl">
                                                    {imageCropSrc ? (
                                                    <div className="w-full h-full" style={{ position: 'relative' }}>
                                                        <img
                                                                src={imageCropSrc}
                                                            alt="After transformation"
                                                            className="w-full h-full object-contain"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center   md:text-xl">Image not available</div>
                                                )}
                                            </div>

                                            <div
                                                className="before-image absolute top-0 left-0 h-full"
                                                style={{
                                                    width: `${progress}%`,
                                                    overflow: 'hidden',
                                                    borderRight: '1px solid white'
                                                }}
                                            >
                                                    {imageComboSrc ? (
                                                    <div className="w-full h-full" style={{ position: 'relative' }}>
                                                        <img
                                                                src={imageComboSrc}
                                                            alt="Before transformation"
                                                            className="absolute top-0 left-0 h-full object-contain"
                                                            style={{
                                                                width: `${100 / (progress / 100)}%`, // Scale proportionally to container
                                                                maxWidth: 'none'
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center   md:text-xl">Image not available</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>


                            </div>

                            <div className="mt-5 w-full px-4">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={progress}
                                    onChange={handleSliderChange}
                                    className="slider w-full"
                                    style={{ "--progress": `${progress}%` }}
                                    disabled={isLoading || !!error}
                                />
                            </div>
                        </div>

                        <div className="px-5 pt-2 flex justify-between w-full max-w-[800px] text-claraview-tertiary mx-auto">
                            <div className='  font-normal text-xs md:text-base '>Before</div>
                            <div className='  font-normal text-xs md:text-base '>After</div>
                        </div>
                    </div>

                    <div className='text-center font-normal   pb-2 text-[#2C2D24] text-base md:text-xl '>
                        Slide from left to right to compare <br /> before and after.
                    </div>



                    <div className='px-12 font-normal text-xs text-center   text-[#808F90] tracking-wider my-4 mb-8 md:text-base'>
                        AI-generated results for informational purposes only. Outcomes may vary. Consult a professional for personalized advice.
                    </div>
                </>
            }
        </div>
    );
}

export default DentistView;
